// Socks levels. Coordinates: 1200 x 800. The ball is a rolled-up sock, the goal is the laundry basket.
// The id is fixed (progress is saved under it) – never change it once the level is released.
import { defineLevels } from '../../core/registry';
import { on } from '../../core/level-helpers';
import type { LevelData, StaticData } from '../../core/types';

const P = 15; // sock ball radius
const floor: StaticData = { x: 600, y: 785, w: 1200, h: 40, skin: 'socks_floor' };
const wallR: StaticData = { x: 1160, y: 400, w: 40, h: 800, skin: 'socks_side' };
const L: LevelData[] = [];
const E = 'socks_elastic', U = 'socks_wool_sock', KL = 'socks_clothespin';

{ // 1 – First hop – new tool: the elastic; the sock drops into an open drawer and the elastic throws it back up
  const s1: StaticData = { x: 200, y: 260, w: 340, h: 22, a: 8, skin: 'socks_shelf' };
  const s2: StaticData = { x: 820, y: 400, w: 260, h: 22, a: 5, skin: 'socks_shelf' };
  L.push({
    id: 'first-hop', par: 1, tools: { [E]: 1 }, learn: ['spring', 'elastic'], intro: E,
    statics: [s1, s2, floor, { x: 540, y: 640, w: 330, h: 40, skin: 'socks_drawer' }, { x: 810, y: 600, w: 200, h: 380, skin: 'socks_side' },
      { x: 1015, y: 580, w: 210, h: 24, skin: 'socks_shelf' }, { x: 1140, y: 440, w: 36, h: 300, skin: 'socks_side' }],
    balls: [on(s1, -120, P)],
    goal: { type: 'bowl', x: 1015, y: 566, w: 190 },
    objs: [{ t: 'duck', x: 640, y: 250 }],
    solution: [{ t: E, x: 540, y: 590, a: 0 }],
  });
}
{ // 2 – The catapult – new tool: the clothespin; pull the pocket back with it and the elastic shoots the sock away
  L.push({
    id: 'slingshot', par: 1, tools: { [KL]: 1 }, learn: ['elastic', 'energy'], intro: KL,
    statics: [floor, wallR, { x: 415, y: 736, w: 830, h: 58, skin: 'socks_drawer' }, { x: 230, y: 683, w: 110, h: 50, skin: 'socks_stool' }],
    balls: [{ x: 230, y: 540, r: P }],
    goal: { type: 'bowl', x: 985, y: 765, w: 290 },
    objs: [{ t: 'socks_slingshot', x: 230, y: 540, base: 118 }, { t: 'duck', x: 560, y: 330 }],
    solution: [{ t: KL, x: 100, y: 640 }],
  });
}
{ // 3 – The woolly sock – new tool: the wool sock; a soft sock stops the flying sock so it drops into the basket
  const s1: StaticData = { x: 250, y: 220, w: 480, h: 22, a: 24, skin: 'socks_shelf' };
  L.push({
    id: 'wool-sock', par: 1, tools: { [U]: 1 }, learn: ['materials', 'elastic'], intro: U,
    statics: [floor, s1, { x: 480, y: 560, w: 40, h: 410, skin: 'socks_side' }],
    balls: [on(s1, -200, P)],
    goal: { type: 'bowl', x: 680, y: 765, w: 220 },
    objs: [{ t: 'duck', x: 575, y: 370 }],
    solution: [{ t: U, x: 660, y: 440, a: 90 }],
  });
}
{ // 4 – Long shot – the basket is far away: pull the elastic far back
  L.push({
    id: 'long-shot', par: 1, tools: { [KL]: 1 }, learn: ['elastic', 'energy'],
    statics: [floor, wallR, { x: 200, y: 662, w: 120, h: 206, skin: 'socks_stool' }],
    balls: [{ x: 200, y: 440, r: P }],
    goal: { type: 'bowl', x: 1010, y: 765, w: 240 },
    objs: [{ t: 'socks_slingshot', x: 200, y: 440, base: 118 }, { t: 'duck', x: 600, y: 250 }],
    solution: [{ t: KL, x: 80, y: 620 }],
    fails: [[{ t: KL, x: 120, y: 500 }]],
  });
}
{ // 5 – Short shot – the basket is close: pull only a little
  L.push({
    id: 'short-shot', par: 1, tools: { [KL]: 1 }, learn: ['elastic', 'energy'],
    statics: [{ x: 360, y: 785, w: 720, h: 40, skin: 'socks_floor' }, { x: 200, y: 662, w: 120, h: 206, skin: 'socks_stool' }],
    balls: [{ x: 200, y: 440, r: P }],
    goal: { type: 'bowl', x: 500, y: 765, w: 200 },
    objs: [{ t: 'socks_slingshot', x: 200, y: 440, base: 118 }, { t: 'duck', x: 330, y: 380 }],
    solution: [{ t: KL, x: 120, y: 520 }],
    fails: [[{ t: KL, x: 60, y: 620 }]],
  });
}
{ // 6 – Out of the hole – elastic or wool? the sock has to get up out of the hole
  const s1: StaticData = { x: 180, y: 260, w: 300, h: 22, a: 10, skin: 'socks_shelf' };
  L.push({
    id: 'out-of-the-hole', par: 1, tools: { [E]: 1, [U]: 1 }, learn: ['materials', 'spring'],
    statics: [floor, wallR, s1, { x: 400, y: 622, w: 40, h: 286, skin: 'socks_side' }, { x: 760, y: 622, w: 40, h: 286, skin: 'socks_side' },
      { x: 870, y: 520, w: 220, h: 24, a: 6, skin: 'socks_shelf' }, { x: 1040, y: 650, w: 200, h: 24, skin: 'socks_shelf' }],
    balls: [on(s1, -110, P)],
    goal: { type: 'bowl', x: 1040, y: 638, w: 180 },
    objs: [{ t: 'duck', x: 530, y: 260 }],
    solution: [{ t: E, x: 570, y: 700, a: 0 }],
    fails: [[{ t: U, x: 570, y: 700, a: 0 }], [{ t: U, x: 570, y: 700, a: 90 }]],
  });
}
{ // 7 – Stop the hop – the sock flies: the soft wool sock catches it, the elastic throws it away (level 3 mirrored)
  const s1: StaticData = { x: 950, y: 220, w: 480, h: 22, a: -24, skin: 'socks_shelf' };
  L.push({
    id: 'stop-the-hop', par: 1, tools: { [E]: 1, [U]: 1 }, learn: ['materials'],
    statics: [{ x: 680, y: 785, w: 1040, h: 40, skin: 'socks_floor' }, s1, { x: 1100, y: 330, w: 40, h: 200, skin: 'socks_side' }],
    balls: [on(s1, 200, P)],
    goal: { type: 'bowl', x: 520, y: 765, w: 220 },
    objs: [{ t: 'duck', x: 625, y: 370 }],
    solution: [{ t: U, x: 540, y: 440, a: 90 }],
    fails: [[{ t: E, x: 540, y: 440, a: 45 }], [{ t: E, x: 540, y: 440, a: -45 }], [{ t: E, x: 540, y: 440, a: 0 }]],
  });
}
{ // 8 – Wonky hop – the sock falls straight down: tilt the elastic so the bounce goes towards the basket
  L.push({
    id: 'crooked-hop', par: 1, tools: { [E]: 1 }, learn: ['direction', 'spring'],
    statics: [floor, wallR, { x: 380, y: 75, w: 200, h: 50, skin: 'socks_drawer' }, { x: 900, y: 532, w: 220, h: 24, skin: 'socks_shelf' }],
    balls: [{ x: 380, y: 130, r: P }],
    goal: { type: 'bowl', x: 900, y: 520, w: 200 },
    objs: [{ t: 'duck', x: 640, y: 260 }],
    solution: [{ t: E, x: 380, y: 620, a: 15 }],
    fails: [[{ t: E, x: 380, y: 620, a: 0 }], [{ t: E, x: 380, y: 620, a: -15 }]],
  });
}
{ // 9 – Over the chest of drawers – aim high with the catapult
  L.push({
    id: 'over-the-dresser', par: 1, tools: { [KL]: 1 }, learn: ['direction', 'elastic'],
    statics: [floor, wallR, { x: 200, y: 712, w: 130, h: 106, skin: 'socks_stool' }, { x: 600, y: 600, w: 200, h: 340, skin: 'socks_side' }, { x: 600, y: 420, w: 230, h: 30, skin: 'socks_drawer' }],
    balls: [{ x: 200, y: 540, r: P }],
    goal: { type: 'bowl', x: 1010, y: 765, w: 240 },
    objs: [{ t: 'socks_slingshot', x: 200, y: 540, base: 118 }, { t: 'duck', x: 420, y: 200 }],
    solution: [{ t: KL, x: 80, y: 700 }],
    fails: [[{ t: KL, x: 60, y: 560 }], [{ t: KL, x: 100, y: 600 }]],
  });
}
{ // 10 – Double hop – the first elastic sends the sock over to the hole, the second throws it up into the basket (the hanger is a decoy)
  L.push({
    id: 'double-hop', par: 2, tools: { [E]: 2, plank: 1 }, learn: ['direction', 'spring'],
    statics: [floor, wallR, { x: 260, y: 75, w: 200, h: 50, skin: 'socks_drawer' }, { x: 640, y: 650, w: 40, h: 250, skin: 'socks_side' }, { x: 880, y: 650, w: 40, h: 250, skin: 'socks_side' },
      { x: 1030, y: 612, w: 220, h: 24, skin: 'socks_shelf' }],
    balls: [{ x: 260, y: 130, r: P }],
    goal: { type: 'bowl', x: 1030, y: 600, w: 190 },
    objs: [{ t: 'duck', x: 760, y: 300 }],
    solution: [{ t: E, x: 260, y: 620, a: 15 }, { t: E, x: 800, y: 640, a: 15 }],
    fails: [[{ t: E, x: 260, y: 620, a: 15 }], [{ t: E, x: 760, y: 700, a: 15 }]],
  });
}
{ // 11 – Soft landing – the trampoline throws the sock away; a wool sock gives a soft landing into the basket
  L.push({
    id: 'soft-landing', par: 1, tools: { [E]: 1, [U]: 1 }, learn: ['materials', 'energy'],
    statics: [floor, wallR, { x: 300, y: 75, w: 200, h: 50, skin: 'socks_drawer' }],
    fixed: [{ t: E, x: 300, y: 700, a: 0 }],
    balls: [{ x: 300, y: 130, r: P }],
    goal: { type: 'bowl', x: 480, y: 765, w: 170 },
    objs: [{ t: 'duck', x: 420, y: 560 }],
    solution: [{ t: U, x: 330, y: 480, a: 30 }],
    fails: [[{ t: E, x: 330, y: 480, a: 30 }], [{ t: E, x: 330, y: 480, a: 15 }]],
  });
}
{ // 12 – Bank shot – under the table the sock can't be shot high: it has to bounce up off the elastic (the hanger is a decoy)
  L.push({
    id: 'bank-shot', par: 2, tools: { [KL]: 1, [E]: 1, plank: 1 }, learn: ['direction', 'elastic'],
    statics: [floor, wallR, { x: 380, y: 555, w: 760, h: 40, skin: 'socks_drawer' }, { x: 1030, y: 552, w: 220, h: 24, skin: 'socks_shelf' }],
    balls: [{ x: 250, y: 645, r: P }],
    goal: { type: 'bowl', x: 1030, y: 540, w: 190 },
    objs: [{ t: 'socks_slingshot', x: 250, y: 645, base: 118 }, { t: 'duck', x: 900, y: 470 }],
    solution: [{ t: KL, x: 40, y: 640 }, { t: E, x: 800, y: 730, a: -30 }],
    fails: [[{ t: KL, x: 40, y: 640 }], [{ t: KL, x: 40, y: 640 }, { t: 'plank', x: 800, y: 730, a: -30 }]],
  });
}
{ // 13 – A pair of socks – two socks must both bounce up into the basket: one elastic each (the hanger is a decoy)
  const sa: StaticData = { x: 120, y: 230, w: 200, h: 22, a: 8, skin: 'socks_shelf' }, sb: StaticData = { x: 1080, y: 230, w: 200, h: 22, a: -8, skin: 'socks_shelf' };
  L.push({
    id: 'pair-of-socks', par: 2, tools: { [E]: 2, plank: 1 }, learn: ['counting', 'direction', 'spring'],
    statics: [floor, sa, sb, { x: 600, y: 600, w: 200, h: 330, skin: 'socks_side' }, { x: 600, y: 430, w: 230, h: 24, skin: 'socks_drawer' }],
    balls: [on(sa, -60, P), on(sb, 60, P, { skin: 'thick' })],
    goal: { type: 'bowl', x: 600, y: 418, w: 200, count: 2 },
    objs: [{ t: 'duck', x: 600, y: 160 }],
    solution: [{ t: E, x: 300, y: 560, a: 0 }, { t: E, x: 900, y: 560, a: 0 }],
    fails: [[{ t: E, x: 300, y: 560, a: 0 }], [{ t: E, x: 900, y: 560, a: 0 }]],
  });
}
{ // 14 – The ball of wool – the ball of yarn tips the sock up on the seesaw, the fixed elastic up by the wall sends it sideways, the placed elastic throws it over the edge (the wool sock is a decoy)
  L.push({
    id: 'ball-of-yarn', par: 2, tools: { stone: 1, [E]: 1, [U]: 1 }, learn: ['lever', 'spring', 'energy'], intro: 'seesaw',
    statics: [floor, wallR, { x: 400, y: 700, w: 60, h: 130, skin: 'socks_stool' }, { x: 278, y: 645, w: 10, h: 40, skin: 'socks_side' },
      { x: 820, y: 700, w: 40, h: 130, skin: 'socks_side' }],
    fixed: [{ t: 'seesaw', x: 400, y: 620, a: -20 }, { t: E, x: 300, y: 380, a: 150 }],
    balls: [{ x: 305, y: 628, r: P }],
    goal: { type: 'bowl', x: 980, y: 765, w: 240 },
    objs: [{ t: 'duck', x: 620, y: 440 }],
    solution: [{ t: 'stone', x: 505, y: 200 }, { t: E, x: 690, y: 630, a: 0 }],
    fails: [[{ t: 'stone', x: 505, y: 200 }], [{ t: 'stone', x: 505, y: 200 }, { t: U, x: 690, y: 630, a: 0 }], [{ t: E, x: 690, y: 630, a: 0 }]],
  });
}
{ // 15 – Master – two socks into the basket: one sits in the catapult, the other rolls the wrong way (wool sock and hangers are decoys)
  const sb: StaticData = { x: 240, y: 200, w: 260, h: 22, a: -8, skin: 'socks_shelf' };
  L.push({
    id: 'master', par: 3, tools: { [KL]: 1, [E]: 2, [U]: 1, plank: 2 }, learn: ['elastic', 'direction', 'materials', 'counting'],
    statics: [floor, wallR, { x: 12, y: 400, w: 24, h: 800, skin: 'socks_side' }, sb, { x: 300, y: 715, w: 110, h: 100, skin: 'socks_stool' }, { x: 640, y: 630, w: 40, h: 270, skin: 'socks_side' }],
    balls: [{ x: 300, y: 547, r: P }, on(sb, 90, P, { skin: 'thick' })],
    goal: { type: 'bowl', x: 950, y: 765, w: 260, count: 2 },
    objs: [{ t: 'socks_slingshot', x: 300, y: 547, base: 118 }, { t: 'duck', x: 640, y: 120 }],
    solution: [{ t: KL, x: 140, y: 680 }, { t: E, x: 100, y: 700, a: 15 }, { t: E, x: 540, y: 520, a: 0 }],
    fails: [[{ t: KL, x: 140, y: 680 }], [{ t: E, x: 100, y: 700, a: 15 }, { t: E, x: 540, y: 520, a: 0 }], [{ t: KL, x: 140, y: 680 }, { t: E, x: 100, y: 700, a: 15 }]],
  });
}

export const levels = defineLevels(L);
